import type { ReactNode } from 'react';

export interface SegmentedControlProps<T extends string> {
  readonly options: readonly T[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Optional label transformer (e.g. capitalize). Default: identity. */
  readonly renderLabel?: (option: T) => ReactNode;
  readonly className?: string;
  readonly ariaLabel?: string;
}

/**
 * iOS-style segmented control. Track is surface-3, the active item
 * gets a surface-1 background with a subtle shadow.
 *
 * Generic over the option type so callers can pass a string union
 * (e.g. 'system' | 'light' | 'dark') and get type-safe onChange.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  renderLabel,
  className = '',
  ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={[
        'inline-grid gap-[3px] rounded-[10px] bg-surface-3 p-[3px]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((opt) => {
        const isActive = opt === value;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => {
              if (!isActive) onChange(opt);
            }}
            className={[
              'min-w-0 truncate rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors duration-fast ease-press',
              'active:scale-[0.97]',
              isActive
                ? 'bg-surface-1 text-text-primary shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                : 'text-text-secondary hover:text-text-primary',
            ].join(' ')}
          >
            {renderLabel ? renderLabel(opt) : opt}
          </button>
        );
      })}
    </div>
  );
}
